/**
 * EMR Action Handler Hook
 *
 * Core business logic for executing EMR actions.
 * Handles all action kinds: api, dialog, event, guard
 *
 * Single Responsibility: Action execution logic
 *
 * Usage:
 * const actionHandler = useEmrActionHandler({
 *   runtime,
 *   practitionerRole,
 *   sectionData,
 *   setSectionData
 * })
 */

import { useCallback, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { evaluateExpression } from '../utils/emrExpressionEvaluator'
import { applyTemplate, buildTemplateContext } from '../utils/emrTemplateEngine'
import { mentorApiURL, commonHeadersMPWeb } from 'src/mentor/services'

export default function useEmrActionHandler({
  runtime = {},
  practitionerRole = null,
  sectionData = null,
  setSectionData = null,
}) {
  const [dialogState, setDialogState] = useState({ open: false, action: null, context: null })

  /**
   * Check if action should be visible
   */
  const shouldShowAction = useCallback(
    (action, context = {}) => {
      // Check role-based permissions
      if (action.roles && action.roles.length > 0) {
        if (!practitionerRole || !action.roles.includes(practitionerRole)) {
          return false
        }
      }

      // Check visibilityWhen expression
      if (action.visibilityWhen) {
        const fullContext = {
          ...runtime,
          ...context,
          // Ensure item has default state for visibility expressions
          item: context.item ? {
            ...context.item,
            state: context.item.state || 'PENDING',
          } : null,
          section: sectionData,
        }
        return evaluateExpression(action.visibilityWhen, fullContext)
      }

      return true
    },
    [practitionerRole, runtime, sectionData]
  )

  /**
   * Check if action should be enabled
   */
  const isActionEnabled = useCallback(
    (action, context = {}) => {
      // If not visible, also not enabled
      if (!shouldShowAction(action, context)) {
        return false
      }

      // Check enableWhen expression
      if (action.enableWhen) {
        const fullContext = {
          ...runtime,
          ...context,
          // Ensure item has default state
          item: context.item ? {
            ...context.item,
            state: context.item.state || 'PENDING',
          } : null,
          section: sectionData,
        }
        return evaluateExpression(action.enableWhen, fullContext)
      }

      return true
    },
    [shouldShowAction, runtime, sectionData]
  )

  /**
   * Evaluate guard condition
   */
  const evaluateGuardCondition = useCallback(
    (guardDef, context = {}) => {
      if (!guardDef || !guardDef.when) {
        return false
      }

      const fullContext = {
        ...runtime,
        ...context,
        // Ensure item has default state if present
        item: context.item ? {
          ...context.item,
          state: context.item.state || 'PENDING',
        } : null,
        section: sectionData,
      }

      return evaluateExpression(guardDef.when, fullContext)
    },
    [runtime, sectionData]
  )

  /**
   * Execute API action
   */
  const executeApiAction = useCallback(
    async (apiDef, context = {}) => {
      const token = localStorage.getItem('token')

      // Build request
      const templateContext = buildTemplateContext({
        runtime,
        item: context.item,
        sectionData,
        formData: context.formData || {},
      })

      const url = applyTemplate(apiDef.path, templateContext)
      const body = applyTemplate(apiDef.body, templateContext)
      const method = (apiDef.method || 'POST').toUpperCase()

      // Apply optimistic update if defined
      let rollbackData = null
      if (apiDef.optimisticUpdate && setSectionData) {
        rollbackData = sectionData
        applyOptimisticUpdate(apiDef.optimisticUpdate, context.item, templateContext)
      }

      try {
        // Make API request
        const response = await axios({
          method,
          url: `${mentorApiURL}${url}`,
          data: body,
          headers: {
            Authorization: `Bearer ${token}`,
            ...commonHeadersMPWeb,
            'ef-user-country-code': localStorage.getItem('countryCode')?.toLowerCase(),
            'ef-app-language': sessionStorage.getItem('languageCode'),
          },
        })

        // Show success toast
        if (apiDef.toast?.success) {
          toast.success(apiDef.toast.success)
        }

        return { success: true, data: response.data }
      } catch (error) {
        console.error('[useEmrActionHandler] API error:', error)

        // Rollback optimistic update
        if (rollbackData && setSectionData) {
          setSectionData(rollbackData)
        }

        // Show error toast
        const errorMessage = apiDef.toast?.error || 'Action failed. Please try again.'
        toast.error(errorMessage)

        return { success: false, error }
      }
    },
    [runtime, sectionData, setSectionData]
  )

  /**
   * Apply optimistic update to section data
   */
  const applyOptimisticUpdate = useCallback(
    (optimisticUpdate, item, templateContext) => {
      if (!setSectionData || !optimisticUpdate) return

      setSectionData((prevData) => {
        // Clone the data
        const newData = JSON.parse(JSON.stringify(prevData))

        // Apply each update
        Object.entries(optimisticUpdate).forEach(([path, value]) => {
          const processedValue = applyTemplate(value, templateContext)
          setNestedValue(newData, path, processedValue, item)
        })

        return newData
      })
    },
    [setSectionData]
  )

  /**
   * Set nested value in object using dot notation
   * Handles paths like "item.state" or "item.notes"
   */
  const setNestedValue = (obj, path, value, item) => {
    const parts = path.split('.')

    // If path starts with "item.", update the specific item
    if (parts[0] === 'item' && item) {
      const propertyPath = parts.slice(1)
      let target = item

      for (let i = 0; i < propertyPath.length - 1; i++) {
        target = target[propertyPath[i]]
      }

      target[propertyPath[propertyPath.length - 1]] = value
    } else {
      // Update section-level property
      let target = obj

      for (let i = 0; i < parts.length - 1; i++) {
        target = target[parts[i]]
      }

      target[parts[parts.length - 1]] = value
    }
  }

  /**
   * Open dialog for user input
   */
  const openDialog = useCallback((action, context = {}) => {
    setDialogState({
      open: true,
      action,
      context,
    })
  }, [])

  /**
   * Close dialog
   */
  const closeDialog = useCallback(() => {
    setDialogState({ open: false, action: null, context: null })
  }, [])

  /**
   * Handle dialog submission
   */
  const handleDialogSubmit = useCallback(
    async (formData) => {
      const { action, context } = dialogState

      if (!action || !action.dialog?.then) {
        closeDialog()
        return
      }

      // Execute the "then" action with form data
      const thenAction = action.dialog.then
      const updatedContext = { ...context, formData }

      closeDialog()

      // Execute based on "then" action kind
      await executeAction(thenAction, updatedContext)
    },
    [dialogState]
  )

  /**
   * Execute event action
   * Emits custom event that can be handled by parent components
   */
  const executeEventAction = useCallback((eventDef, context = {}) => {
    if (!eventDef || !eventDef.name) {
      console.warn('[useEmrActionHandler] Invalid event definition')
      return
    }

    // Dispatch custom event
    const event = new CustomEvent(eventDef.name, {
      detail: {
        context,
        runtime,
        sectionData,
      },
    })

    window.dispatchEvent(event)

    console.log('[useEmrActionHandler] Event dispatched:', eventDef.name, event.detail)
  }, [runtime, sectionData])

  /**
   * Main action executor
   * Routes to appropriate handler based on action.kind
   */
  const executeAction = useCallback(
    async (action, context = {}) => {
      if (!action) return

      console.log('[useEmrActionHandler] Executing action:', {
        id: action.id,
        kind: action.kind,
        label: action.label,
        context,
      })

      switch (action.kind) {
        case 'api':
          if (action.api) {
            return await executeApiAction(action.api, context)
          }
          break

        case 'dialog':
          if (action.dialog) {
            openDialog(action, context)
          }
          break

        case 'event':
          if (action.event) {
            executeEventAction(action.event, context)
          }
          break

        case 'guard':
          // Guard actions are special - they're checked before save
          // Not executed directly by user click
          console.warn('[useEmrActionHandler] Guard actions should not be executed directly')
          break

        default:
          console.warn('[useEmrActionHandler] Unknown action kind:', action.kind)
      }
    },
    [executeApiAction, openDialog, executeEventAction]
  )

  return {
    // Core execution
    executeAction,

    // Visibility/enablement checks
    shouldShowAction,
    isActionEnabled,

    // Guard evaluation
    evaluateGuardCondition,

    // Dialog management
    dialogState,
    openDialog,
    closeDialog,
    handleDialogSubmit,
  }
}
